import { useQueries, UseQueryOptions, UseQueryResult } from "react-query";

import { isNotFoundError, unauthorizedErrorMessage } from "lib/axios/constants";

/*
Below is a wrapper to add typing to useQueries.

https://blog.johnnyreilly.com/2021/01/03/strongly-typing-react-query-s-usequeries/

https://github.com/tannerlinsley/react-query/pull/1527
*/
type Awaited<T> = T extends PromiseLike<infer U> ? Awaited<U> : T;

export function useQueriesTyped<TQueries extends readonly UseQueryOptions[]>(
  queries: [...TQueries],
): {
  [ArrayElement in keyof TQueries]: UseQueryResult<
    TQueries[ArrayElement] extends { select: infer TSelect }
      ? TSelect extends (data: any) => any
        ? ReturnType<TSelect>
        : never
      : Awaited<
          ReturnType<
            NonNullable<
              Extract<TQueries[ArrayElement], UseQueryOptions>["queryFn"]
            >
          >
        >
  >;
} {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useQueries(
    queries as UseQueryOptions<unknown, unknown, unknown>[],
  ) as any;
}

export function skipRetriesOn403<TError extends { message: string }>(
  failureCount: number,
  error: TError,
) {
  if (error.message === unauthorizedErrorMessage) {
    return false;
  }
  return failureCount < 3;
}

export function skipRetriesOn404<TError extends { message: string }>(
  failureCount: number,
  error: TError,
) {
  if (isNotFoundError(error.message)) {
    return false;
  }
  return failureCount < 3;
}
